import { UserMinus, UserPlus } from 'lucide-react';
import { slotMemberLabel, type Discipline, type MemberGender } from '@bm/contracts';
import { genderBgClass } from '../../lib/gender-colors';

export interface AssignedPlayer {
  memberId: string;
  name: string;
  gender: MemberGender | null;
  appearanceCount: number;
}

/**
 * Eine einzelne Spielerposition (member1 oder member2) innerhalb einer
 * Disziplin-Instanz — z. B. "Spieler 1" im "1. Herrendoppel". Funktioniert
 * vollständig über Tap/Klick (öffnet den Auswahl-Drawer, Kernweg auf
 * Mobile UND Desktop) und zusätzlich per nativem HTML5-Drag&Drop als
 * Beschleuniger für Maus-Nutzer — nie ausschließlich Drag&Drop (§21/§6).
 *
 * Bewusst eine einzelne kompakte Zeile (Label + Inhalt nebeneinander statt
 * gestapelt), Label einzeilig ("Spieler 1" statt umbrechend "Spieler"/"1").
 */
export function LineupSlotBox({
  discipline,
  memberSlot,
  player,
  disabled,
  isDragActive,
  isValidDropTarget,
  onOpenDrawer,
  onRemove,
  onDrop,
}: {
  discipline: Discipline;
  memberSlot: 1 | 2;
  player: AssignedPlayer | null;
  disabled: boolean;
  isDragActive: boolean;
  isValidDropTarget: boolean;
  onOpenDrawer: () => void;
  onRemove: () => void;
  onDrop: () => void;
}) {
  const label = slotMemberLabel(discipline, memberSlot);
  const showDropHighlight = isDragActive && isValidDropTarget;
  const showDropDisabled = isDragActive && !isValidDropTarget;

  return (
    <div
      onDragOver={(e) => {
        if (!isValidDropTarget) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (isValidDropTarget) onDrop();
      }}
      className={`flex min-h-9 min-w-0 items-center gap-1.5 rounded-lg border-2 pl-2 pr-1 transition-colors ${
        showDropHighlight
          ? 'border-brand-accent bg-brand-accent/5'
          : showDropDisabled
            ? 'border-dashed border-slate-200 bg-slate-50 opacity-50'
            : player
              ? genderBgClass(player.gender)
              : 'border-slate-200 bg-slate-50'
      }`}
    >
      <span className="w-16 shrink-0 whitespace-nowrap text-[10px] font-medium uppercase leading-tight text-slate-400">
        {label}
      </span>
      {player ? (
        <>
          <button
            type="button"
            disabled={disabled}
            onClick={onOpenDrawer}
            className="min-w-0 flex-1 truncate rounded px-1 py-1.5 text-left text-sm font-medium text-slate-800 hover:bg-white disabled:hover:bg-transparent"
            title={`${player.name} ändern`}
          >
            {player.name}
          </button>
          {!disabled && (
            <button
              type="button"
              title={`${player.name} entfernen`}
              aria-label={`${player.name} aus ${label} entfernen`}
              onClick={onRemove}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
            >
              <UserMinus size={14} />
            </button>
          )}
        </>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={onOpenDrawer}
          className="flex min-h-9 flex-1 items-center gap-1.5 truncate rounded-md px-1 py-1.5 text-left text-sm text-slate-400 hover:text-brand-accent disabled:hover:text-slate-400"
        >
          <UserPlus size={14} className="shrink-0" />
          <span className="truncate">Spieler zuweisen</span>
        </button>
      )}
    </div>
  );
}
